import { Button, Notice } from '@buffer-mono/popcorn'
import * as React from 'react'
import { useEffect, type MouseEventHandler } from 'react'

import { useCta } from '~publish/hooks/useCta'
import { SEGMENT_NAMES } from '~publish/legacy/constants'
import useQueueLimitBar from '~publish/legacy/queue/components/QueueLimitBar/useQueueLimitBar'

import { useRegisteredBanners } from '~publish/components/RegisteredBanner'
import styles from './QueueLimitNotice.module.css'
const PLAN_SELECTOR_ID = 'plan_selector_container'
const CTA = SEGMENT_NAMES.QUEUE_LIMIT_BANNER_UPGRADE
const UPGRADE_PATH_NAME = 'queueLimit-upgrade'

export const QueueLimitNotice = (): JSX.Element | null => {
  const { numberOfScheduledPosts, postLimit, shouldShow } = useQueueLimitBar()
  const cta = useCta(CTA as CTA, {
    upgradePathName: UPGRADE_PATH_NAME,
  })

  // Track the upgrade path view for the queue limit notice
  useEffect(() => {
    cta.viewed()
  }, [cta])

  // Suppress banners if this notice is visible
  const { suppressBanners, unsuppressBanners } = useRegisteredBanners()
  React.useLayoutEffect(() => {
    if (shouldShow) {
      suppressBanners()
    }
    return (): void => unsuppressBanners()
  }, [shouldShow, suppressBanners, unsuppressBanners])

  if (!shouldShow) {
    return null
  }

  const handleUpgradeClick: MouseEventHandler<HTMLButtonElement> = (
    evt,
  ): void => {
    evt.preventDefault()
    cta.clicked()

    const { MODALS, actions } = window?.appshell || {}
    actions.openModal(MODALS.planSelector, {
      cta: CTA,
      upgradePathName: UPGRADE_PATH_NAME,
    })
  }

  const postsRemaining = postLimit - numberOfScheduledPosts
  const reachedLimit = numberOfScheduledPosts >= postLimit
  const variant = reachedLimit ? 'upgrade' : 'info'

  const headingCopy = `${postsRemaining <= 0 ? 0 : postsRemaining} ${
    postsRemaining > 1 ? 'Posts' : 'Post'
  } left to schedule on free plan.`

  const buttonCopy = 'Upgrade for Unlimited Posts'

  return (
    <Notice
      data-testid="queue-limit-notice"
      variant={variant}
      data-state={variant}
      className={styles.notice}
    >
      <Notice.Text className={styles.text}>
        {headingCopy}
        <Button
          variant={variant === 'upgrade' ? 'upgrade' : 'primary'}
          size="small"
          onClick={handleUpgradeClick}
          aria-haspopup="dialog"
          aria-controls={PLAN_SELECTOR_ID}
        >
          {buttonCopy}
        </Button>
      </Notice.Text>
    </Notice>
  )
}
